import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock3, XCircle, RefreshCw } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
const fmtSP = (iso, opts) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", ...opts }) : null;

export default function PagamentoRetorno() {
  const [params] = useSearchParams();
  const invoiceId = params.get("invoice_id");
  const mpStatus = (params.get("collection_status") || params.get("status") || "").toLowerCase();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!invoiceId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/invoices/${invoiceId}/public-status`);
      setInv(data);
    } catch { setInv(null); }
    setLoading(false);
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const rejected = ["rejected", "failure", "cancelled"].includes(mpStatus) && inv?.status !== "paid";
  const approved = inv?.status === "paid";
  const retryLink = inv?.package_plan_id ? `/comprar-plano/${inv.package_plan_id}` : "/espacos";
  const product = inv?.plan_name || inv?.reservation?.space_name || inv?.description || "Sua compra";

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[#F7F7F5]">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center" data-testid="payment-return-card">
        {loading ? (
          <p className="text-gray-500 py-10" data-testid="payment-return-loading">Consultando seu pagamento…</p>
        ) : !invoiceId || !inv ? (
          <>
            <XCircle className="mx-auto text-gray-300" size={56} />
            <h1 className="text-xl font-bold text-[#0A1128] mt-4">Cobrança não encontrada</h1>
            <Link to="/" className="inline-block mt-6 text-sm font-bold text-[#0A1128] underline">Voltar ao início</Link>
          </>
        ) : approved ? (
          <div data-testid="payment-return-approved">
            <CheckCircle2 className="mx-auto text-green-500" size={56} />
            <h1 className="text-2xl font-bold text-[#0A1128] mt-4">✅ Pagamento aprovado!</h1>
            <p className="text-gray-600 mt-1">Sua compra foi confirmada com sucesso.</p>
            <div className="bg-gray-50 rounded-xl p-4 mt-6 text-left text-sm space-y-1.5">
              <div><span className="text-gray-500">Produto:</span> <b className="text-[#0A1128]">{product}</b></div>
              {inv.reservation && (
                <>
                  <div><span className="text-gray-500">Data:</span> <b>{fmtSP(inv.reservation.start_time, { day: "2-digit", month: "2-digit", year: "numeric" })}</b></div>
                  <div><span className="text-gray-500">Horário:</span> <b>{fmtSP(inv.reservation.start_time, { hour: "2-digit", minute: "2-digit" })} às {fmtSP(inv.reservation.end_time, { hour: "2-digit", minute: "2-digit" })}</b></div>
                </>
              )}
              <div><span className="text-gray-500">Valor pago:</span> <b className="text-green-600">{fmtBRL(inv.amount)}</b></div>
            </div>
            <div className="grid gap-2 mt-6">
              <Link to="/portal/reservas" data-testid="btn-my-reservations" className="bg-[#0A1128] text-white rounded-xl py-3 font-bold text-sm hover:bg-[#14213D]">Ver minhas reservas</Link>
              <Link to="/portal" data-testid="btn-my-hours" className="border border-gray-200 rounded-xl py-3 font-bold text-sm text-[#0A1128] hover:bg-gray-50">Meu Banco de Horas</Link>
              <Link to="/planos" data-testid="btn-buy-again" className="text-sm text-gray-500 underline mt-1">Comprar novamente</Link>
            </div>
          </div>
        ) : rejected ? (
          <div data-testid="payment-return-rejected">
            <XCircle className="mx-auto text-red-500" size={56} />
            <h1 className="text-2xl font-bold text-[#0A1128] mt-4">❌ O pagamento não foi concluído.</h1>
            <p className="text-gray-600 mt-1">Nenhum valor foi cobrado. Você pode tentar de novo.</p>
            <div className="grid gap-2 mt-6">
              <Link to={retryLink} data-testid="btn-retry" className="bg-[#0A1128] text-white rounded-xl py-3 font-bold text-sm hover:bg-[#14213D]">Tentar novamente</Link>
              <Link to={retryLink} data-testid="btn-choose-pix" className="border border-gray-200 rounded-xl py-3 font-bold text-sm text-[#0A1128] hover:bg-gray-50">Escolher PIX</Link>
            </div>
          </div>
        ) : (
          <div data-testid="payment-return-pending">
            <Clock3 className="mx-auto text-amber-500" size={56} />
            <h1 className="text-2xl font-bold text-[#0A1128] mt-4">⏳ Aguardando confirmação</h1>
            <p className="text-gray-600 mt-1">
              Estamos aguardando a confirmação do pagamento. Atualizaremos automaticamente assim que o Mercado Pago concluir o processamento.
            </p>
            <button onClick={load} data-testid="btn-refresh-status"
              className="mt-6 inline-flex items-center gap-2 bg-[#0A1128] text-white rounded-xl px-6 py-3 font-bold text-sm hover:bg-[#14213D]">
              <RefreshCw size={16} /> Atualizar Status
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
